'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useAuth } from '@/lib/auth';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';
import { ArchiMateCanvas } from '@/components/archimate/ArchiMateCanvas';
import type { ArchiMateElement, ArchiMateRelationship } from '@/lib/archimate/types';
import Toolbar from '@/components/scenarioplanner/Toolbar';
import PropertiesPanel from '@/components/scenarioplanner/PropertiesPanel';
import SimulationControls from '@/components/scenarioplanner/SimulationControls';
import { runSimulation } from '@/lib/archimate/simulation';

type View = 'landing' | 'scenario-planner';
type Tool = 'select' | 'component' | 'database' | 'edge';

export default function ArchitectureScenarioPlannerPage() {
  const { isAuthenticated, login } = useAuth();
  const [currentView, setCurrentView] = useState<View>('landing');
  const [elements, setElements] = useState<ArchiMateElement[]>([]);
  const [relationships, setRelationships] = useState<ArchiMateRelationship[]>([]);
  const [activeTool, setActiveTool] = useState<Tool>('select');
  const [selectedElement, setSelectedElement] = useState<ArchiMateElement | null>(null);
  const [simulationResults, setSimulationResults] = useState<any | null>(null);
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'architecture-scenario-planner',
    toolCategory: 'planner',
  });

  const handleStartPlanner = () => {
    // Load a default or sample architecture
    const sampleElements: ArchiMateElement[] = [
      { id: '1', type: 'application-component', layer: 'application', name: 'Web Server', x: 200, y: 150, width: 120, height: 60 },
      { id: '2', type: 'application-component', layer: 'application', name: 'App Server', x: 400, y: 150, width: 120, height: 60 },
      { id: '3', type: 'data-object', layer: 'application', name: 'Database', x: 600, y: 150, width: 120, height: 60 },
    ];
    const sampleRelationships: ArchiMateRelationship[] = [
      { id: 'r1', sourceId: '1', targetId: '2', type: 'flow' },
      { id: 'r2', sourceId: '2', targetId: '3', type: 'flow' },
    ];
    setElements(sampleElements);
    setRelationships(sampleRelationships);
    setCurrentView('scenario-planner');
  };

  const handleReset = () => {
    setCurrentView('landing');
    setElements([]);
    setRelationships([]);
    setSimulationResults(null);
  };

  const handleUpdateElement = (id: string, updates: Partial<ArchiMateElement>) => {
    setElements(elements.map(el => el.id === id ? { ...el, ...updates } : el));
  };

  const handleRunSimulation = () => {
    const results = runSimulation(elements, relationships);
    setSimulationResults(results);
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled || elements.length === 0) return;

    const elementScores: Record<string, number> = {};
    elements.forEach(el => {
      elementScores[el.name] = el.type === 'application-component' ? 100 : 80;
    });

    const recommendations = [];
    if (elements.length < 3) {
      recommendations.push({
        id: 'add-components',
        title: 'Add More Components',
        priority: 'medium' as const,
        description: 'Consider adding more components to fully model your architecture.',
      });
    }
    if (relationships.length === 0) {
      recommendations.push({
        id: 'add-relationships',
        title: 'Define Relationships',
        priority: 'high' as const,
        description: 'Add relationships between components to model data flow.',
      });
    }
    if (simulationResults) {
      recommendations.push({
        id: 'simulation-complete',
        title: 'Review Simulation Results',
        priority: 'low' as const,
        description: 'Simulation has been run. Review the results for performance insights.',
      });
    }

    const assessment = await saveResult({
      name: `Architecture Scenario - ${new Date().toLocaleDateString()}`,
      score_overall: simulationResults?.overallScore || (elements.length > 0 ? 70 : 0),
      scores: elementScores,
      answers: {
        elements: elements.map(el => ({ id: el.id, name: el.name, type: el.type, layer: el.layer })),
        relationships: relationships.map(r => ({ sourceId: r.sourceId, targetId: r.targetId, type: r.type })),
      },
      recommendations,
      metadata: {
        elementCount: elements.length,
        relationshipCount: relationships.length,
        hasSimulationResults: !!simulationResults,
        simulationResults: simulationResults || null,
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-background py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-light-text dark:text-foreground mb-4">
            Architecture Scenario Planner
          </h1>
          <p className="text-xl text-light-text-secondary dark:text-foreground-secondary max-w-3xl mx-auto">
            Model, simulate, and compare architectural scenarios to make better design decisions.
          </p>
        </div>

        {currentView !== 'landing' && (
          <div className="mb-8 flex items-center justify-between">
            <nav className="flex items-center space-x-2 text-sm">
              <button
                onClick={handleReset}
                className="text-light-text-secondary dark:text-foreground-secondary hover:text-light-text dark:hover:text-foreground transition-colors"
              >
                Home
              </button>
              <span className="text-light-text-tertiary dark:text-foreground-muted">/</span>
              <span className="text-light-text dark:text-foreground capitalize">{currentView}</span>
            </nav>
            <button
              onClick={handleReset}
              className="px-4 py-2 border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground text-sm hover:bg-light-hover dark:hover:bg-cream/10 transition-colors"
            >
              Start Over
            </button>
          </div>
        )}

        {currentView === 'landing' && (
          <LandingView onStartPlanner={handleStartPlanner} />
        )}

        {currentView === 'scenario-planner' && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-3 h-[600px] border-2 border-dashed border-border rounded-lg bg-surface">
              <ArchiMateCanvas
                elements={elements}
                relationships={relationships}
                onSelectElement={setSelectedElement}
                onUpdateElement={handleUpdateElement}
                tool={activeTool === 'edge' ? 'connect' : 'select'}
              />
            </div>
            <div className="space-y-4">
              <Toolbar onToolSelect={setActiveTool} activeTool={activeTool} />
              <PropertiesPanel selectedElement={selectedElement} onUpdateElement={handleUpdateElement} />
              <SimulationControls onRunSimulation={handleRunSimulation} onResetSimulation={() => setSimulationResults(null)} simulationResults={simulationResults} />

              {/* Command Center Save Button */}
              {elements.length > 0 && (
                <div className="space-y-2">
                  {commandCenterEnabled && !saved && (
                    <button
                      onClick={handleSaveToCommandCenter}
                      disabled={saving}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {saving ? 'Saving...' : 'Save to Command Center'}
                    </button>
                  )}
                  {saved && (
                    <div className="w-full px-4 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center justify-center gap-2 text-sm">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Saved to Command Center
                    </div>
                  )}
                  {saveError && (
                    <div className="w-full px-4 py-3 bg-red-600/20 text-red-400 rounded-lg text-xs">
                      {saveError}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LandingView({ onStartPlanner }: { onStartPlanner: () => void }) {
  return (
    <div className="text-center max-w-4xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
          <div className="text-3xl mb-3">✏️</div>
          <h3 className="font-semibold text-light-text dark:text-foreground mb-2">Model</h3>
          <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
            Visually design your system architecture using ArchiMate components.
          </p>
        </div>
        <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
          <div className="text-3xl mb-3">⚙️</div>
          <h3 className="font-semibold text-light-text dark:text-foreground mb-2">Simulate</h3>
          <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
            Run simulations to estimate the impact of changes on cost, performance, and reliability.
          </p>
        </div>
        <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
          <div className="text-3xl mb-3">📊</div>
          <h3 className="font-semibold text-light-text dark:text-foreground mb-2">Compare</h3>
          <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
            Compare different scenarios to understand trade-offs and make data-driven decisions.
          </p>
        </div>
      </div>

      <button
        onClick={onStartPlanner}
        className="px-8 py-4 bg-accent text-accent-foreground rounded-lg text-lg font-bold hover:bg-accent-hover transition-colors shadow-lg"
      >
        Start Planning
      </button>
    </div>
  );
}
